// TKT-2026-105480. The store's cap on one kiosk order, as the cart applies it.
//
// The owner sets these on POS > Kiosk Settings, and both kiosk config endpoints
// return them (backend/utils/kioskOrderLimits.js). Both are off (null) until set:
//   maxOrderLines  the most DIFFERENT cards one order may hold
//   maxOrderUnits  the most cards in total
//   largeOrderUrl  where a shopper with a bigger order is sent instead
//
// Only purchase lines count. A trade-in line is the store buying, never limited.
//
// This is for the shopper's benefit only. POST /orders applies the same rule and
// is what actually refuses an order, so the two must count the same way: a line
// is one distinct SKU (the product when a line has no SKU).

export const KIOSK_ORDER_LIMIT_MAX = 10000;

const readLimit = (value) => (
    Number.isInteger(value) && value >= 1 && value <= KIOSK_ORDER_LIMIT_MAX ? value : null
);

export function readOrderLimits(config) {
    const cfg = config || {};
    const url = typeof cfg.largeOrderUrl === 'string' ? cfg.largeOrderUrl.trim() : '';
    return {
        maxOrderLines: readLimit(cfg.maxOrderLines),
        maxOrderUnits: readLimit(cfg.maxOrderUnits),
        largeOrderUrl: /^https?:\/\//i.test(url) ? url : '',
    };
}

export const hasOrderLimits = (limits) => Boolean(
    limits && (limits.maxOrderLines !== null || limits.maxOrderUnits !== null)
);

const isSell = (item) => (item?.mode || 'sell') === 'sell';

export const lineKeyOf = (item) => String(item?.variant?.skuId ?? item?.productId);

/** Distinct lines and total units of the purchase side of a cart. */
export function countSellCart(cart) {
    const keys = new Set();
    let units = 0;
    (cart || []).filter(isSell).forEach((item) => {
        keys.add(lineKeyOf(item));
        units += Number(item.qty) || 0;
    });
    return { lines: keys.size, units };
}

/**
 * How many more copies of `item` the cart may take, given the limits alone
 * (stock is capped separately). Infinity when no limit applies.
 */
export function roomFor(cart, item, limits) {
    if (!isSell(item) || !hasOrderLimits(limits)) return Infinity;
    const { lines, units } = countSellCart(cart);
    const isNewLine = !(cart || []).some((p) => isSell(p) && lineKeyOf(p) === lineKeyOf(item));
    if (isNewLine && limits.maxOrderLines !== null && lines >= limits.maxOrderLines) return 0;
    if (limits.maxOrderUnits === null) return Infinity;
    return Math.max(0, limits.maxOrderUnits - units);
}

/** The sentence a shopper sees, naming only the limits the store has set. */
export function describeOrderLimits(limits) {
    const parts = [];
    if (limits?.maxOrderUnits !== null && limits?.maxOrderUnits !== undefined) {
        parts.push(`${limits.maxOrderUnits} ${limits.maxOrderUnits === 1 ? 'card' : 'cards'}`);
    }
    if (limits?.maxOrderLines !== null && limits?.maxOrderLines !== undefined) {
        parts.push(`${limits.maxOrderLines} different ${limits.maxOrderLines === 1 ? 'card' : 'cards'}`);
    }
    if (!parts.length) return '';
    const cap = `Kiosk orders are limited to ${parts.join(' and ')}.`;
    return limits.largeOrderUrl
        ? `${cap} For a larger order, please order online at ${limits.largeOrderUrl}`
        : `${cap} For a larger order, please ask a member of staff.`;
}

/**
 * Where the cart stands against the limits: `atLimit` when nothing more can be
 * added, `over` when checkout would be refused (a cart kept from before the
 * owner set a limit, or one the limit was lowered under).
 */
export function orderLimitStatus(cart, limits) {
    if (!hasOrderLimits(limits)) {
        return { active: false, atLimit: false, over: false, lines: 0, units: 0, message: '' };
    }
    const { lines, units } = countSellCart(cart);
    const lineCap = limits.maxOrderLines;
    const unitCap = limits.maxOrderUnits;
    const over = (lineCap !== null && lines > lineCap) || (unitCap !== null && units > unitCap);
    const atLimit = over || (lineCap !== null && lines >= lineCap) || (unitCap !== null && units >= unitCap);
    return { active: true, atLimit, over, lines, units, message: describeOrderLimits(limits) };
}
